import React, { ReactElement } from 'react';
import {
  addCommasToNumbers,
  percentageToTwoDecimalPlaces,
  toFourDecimalPlaces,
} from '../../../../../utilities/numberFormatters';
import {
  RenderDataType,
  useOverviewSecondLevelStyles,
} from '../OverviewSecondLevelGraphs.component';
import { useWindowSize } from '../../../../../hooks/useWindowSize';
import {
  hexToRGBA,
  mapStatusToColor,
} from '../../../../../utilities/colorUtilities';
import { mainColors } from '../../../../../styling/theme';
import { IGenericLineChartProps } from '../../../../../types/components/charts/lineCharts';
import GenericLineChart from '../../../../charts/GenericLineChart';
import { Status } from '../../../../../types/redux/data/dataTypes';
import { Grid, Typography, useTheme } from '@mui/material';

interface DetailTimeSeriesProps {
  renderData: RenderDataType;
  varFund: boolean;
}

function timeSeriesFormatter(data: any, lines: any) {
  const lineNames = lines.map((line: any) => {
    return {
      name: line.dataKey,
      formatter: line.formatter,
    };
  });

  return data.map((row: any) => {
    const obj: any = {};
    obj.name = row[0];
    lineNames.forEach((lineName: any, index: number) => {
      obj[lineName.name] = lineName.formatter(row[index + 1]);
    });
    return obj;
  });
}

function DetailTimeSeries({
  renderData,
  varFund,
}: DetailTimeSeriesProps): ReactElement {
  const theme = useTheme();
  const classes = useOverviewSecondLevelStyles();

  const secondaryDataKey = varFund ? 'Value at Risk' : 'Commitment';

  function customFormatter(value: number | string) {
    return parseFloat(toFourDecimalPlaces(value as number));
  }
  const chartData = timeSeriesFormatter(renderData.historical_time_series, [
    {
      dataKey: 'NAV',
      formatter: Math.round,
    },
    {
      dataKey: secondaryDataKey,
      formatter: customFormatter,
    },
  ]);

  const { width } = useWindowSize();

  return (
    <Grid item xs={4} className={classes.chart}>
      <Typography variant="h2" className={classes.mapTitle}>
        Historical Global Exposure
      </Typography>
      <div className={classes.chartContainerDiv}>
        <TimeSeriesContainer
          title="Historical Global Exposure"
          width="100%"
          height={width > 1600 ? 400 : 300}
          error={null}
          data={chartData}
          backgroundColor={hexToRGBA(
            mapStatusToColor(
              String(renderData.historical_time_series_status) as Status,
            ),
            0.3,
          )}
          lines={[
            {
              dataKey: 'NAV',
              color: mainColors.Fail,
              yAxisId: 'left',
            },
            {
              dataKey: secondaryDataKey,
              color: theme.palette.primary.main,
              yAxisId: 'right',
            },
          ]}
          yAxisLabel={{
            value: 'NAV',
            angle: -90,
            style: { textAnchor: 'middle', fill: theme.palette.grey[500] },

            position: 'left',
          }}
          yAxisDomain={[
            (dataMin: number) => dataMin - dataMin * 0.1,
            (dataMax: number) => dataMax + dataMax * 0.1,
          ]}
          yAxisTickFormatter={(val: any) => {
            return `${val / 1000000}m`;
          }}
          xAxisLabel={{
            value: 'Date',
            position: 'bottom',
            style: {
              fill: theme.palette.grey[500],
            },
          }}
          additionalYAxis={[
            {
              // ticks: secondaryTickValues,
              leftDataKey: 'globalExposure',
              yAxisId: 'right',
              orientation: 'right',
              domain: [
                (dataMin: any) => {
                  return dataMin - dataMin * 0.1;
                },
                (dataMax: any) => dataMax + dataMax * 0.1,
              ],
              label: {
                value: secondaryDataKey,
                angle: -90,
                position: 'right',
                style: {
                  textAnchor: 'middle',
                  fill: theme.palette.grey[500],
                },
              },
              tickFormatter: (val: any) => {
                return `${(val * 100).toFixed(1)}%`;
              },
            },
          ]}
          margin={{ top: 0, left: 10, right: 40, bottom: 50 }}
          tooltipFormatter={(value: any, name: any) => {
            let returnVal;
            if (name !== 'NAV') {
              returnVal = `${percentageToTwoDecimalPlaces(value)}`;
            } else {
              returnVal = addCommasToNumbers(value);
            }
            return returnVal;
          }}
        />
      </div>
    </Grid>
  );
}

export default DetailTimeSeries;

const TimeSeriesContainer = React.memo((props: IGenericLineChartProps) => {
  return <GenericLineChart {...props} />;
});
